import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { Path, PathFormData, EditorPathActivity } from "@/types/path";
import { findSuggestionRankConflict, parseSuggestionRank } from "@/lib/pathSuggestionRank";

export function usePathEditor(pathId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<PathFormData>({
    title: "",
    pathType: "path",
    themeId: null,
    metadata: null,
    resources: null,
  });
  const [activities, setActivities] = useState<EditorPathActivity[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const { isLoading } = useQuery({
    queryKey: ["path-editor", pathId],
    enabled: !!pathId && !loaded,
    queryFn: async () => {
      const { data: path, error } = await supabase
        .from("path")
        .select("*")
        .eq("id", pathId!)
        .single();
      if (error) throw error;

      const { data: pathActivities, error: paError } = await supabase
        .from("path_activity")
        .select("*, activity(id, title, version, is_published, activity_root_id, estimated_duration_minutes)")
        .eq("path_id", pathId!)
        .order("order_index");
      if (paError) throw paError;

      setFormData({
        title: path.title,
        pathType: path.path_type,
        themeId: path.theme_id,
        metadata: path.metadata as Record<string, unknown> | null,
        resources: (path as any).resources as Record<string, unknown> | null,
      });

      const editorActivities: EditorPathActivity[] = (pathActivities || []).map((pa) => {
        const act = pa.activity as unknown as {
          id: string; title: string; version: number; is_published: boolean; activity_root_id: string | null; estimated_duration_minutes: number | null;
        };
        return {
          localId: crypto.randomUUID(),
          activityId: act.id,
          activityTitle: act.title,
          activityVersion: act.version,
          activityDurationMinutes: act.estimated_duration_minutes,
          orderIndex: pa.order_index,
          existingId: pa.id,
        };
      });

      setActivities(editorActivities);
      setIsPublished(path.is_published);
      setLoaded(true);
      return path;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ publish }: { publish: boolean }) => {
      if (!user) throw new Error("Non authentifié");
      if (!formData.title.trim()) throw new Error("Le titre est requis");

      const suggestionRank = formData.pathType === "path" ? parseSuggestionRank(formData.metadata) : null;
      if (suggestionRank !== null) {
        const { data: rankPaths, error: rankError } = await supabase
          .from("path")
          .select("id, title, path_root_id, path_type, version, is_published, metadata")
          .eq("path_type", "path");
        if (rankError) throw rankError;

        const conflict = findSuggestionRankConflict(rankPaths || [], suggestionRank, pathId);
        if (conflict) {
          throw new Error(`Le rang ${suggestionRank} est déjà utilisé par « ${conflict.title} ».`);
        }
      }

      // Published paths: only allow metadata update (e.g. suggestion_importance_rank)
      if (isPublished && !publish) {
        const existingId = pathId;
        if (!existingId) throw new Error("ID manquant");
        const { data, error } = await supabase
          .from("path")
          .update({ metadata: formData.metadata as any, updated_by: user.id })
          .eq("id", existingId)
          .select()
          .single();
        if (error) throw error;
        return data;
      }

      if (isPublished) throw new Error("Un parcours publié ne peut pas être modifié");

      const pathPayload = {
        title: formData.title.trim(),
        path_type: formData.pathType,
        theme_id: formData.themeId,
        metadata: formData.metadata as any,
        resources: formData.resources as any,
        is_published: publish,
        updated_by: user.id,
      };

      // Helper to cascade publish to all activities and their cards/groups
      const cascadePublish = async (pathId: string) => {
        // Get all linked activity IDs
        const { data: pas } = await supabase
          .from("path_activity")
          .select("activity_id")
          .eq("path_id", pathId);
        if (!pas || pas.length === 0) return;

        const activityIds = pas.map((pa) => pa.activity_id);

        // Publish all activities
        await supabase
          .from("activity")
          .update({ is_published: true, updated_by: user.id })
          .in("id", activityIds);

        // Publish all cards linked via activity_step
        const { data: steps } = await supabase
          .from("activity_step")
          .select("card_id, card_group_id")
          .in("activity_id", activityIds);
        if (!steps) return;

        const cardIds = steps.map((s) => s.card_id).filter(Boolean) as string[];
        const groupIds = steps.map((s) => s.card_group_id).filter(Boolean) as string[];

        // Also get cards from card_group_items
        if (groupIds.length > 0) {
          const { data: groupItems } = await supabase
            .from("card_group_item")
            .select("card_id")
            .in("card_group_id", groupIds);
          if (groupItems) {
            for (const gi of groupItems) cardIds.push(gi.card_id);
          }
        }

        // Note: card and card_group tables don't have is_published column,
        // so no update needed for them. The immutability is enforced by the path/activity level.
      };
      let pathRecord: Path;

      if (pathId) {
        const { data, error } = await supabase
          .from("path")
          .update(pathPayload)
          .eq("id", pathId)
          .select()
          .single();
        if (error) throw error;
        pathRecord = data;

        // Diff path_activity
        const { data: oldPAs } = await supabase
          .from("path_activity")
          .select("id")
          .eq("path_id", pathId);
        const oldIds = new Set((oldPAs || []).map((p) => p.id));
        const keptIds = new Set<string>();

        for (let i = 0; i < activities.length; i++) {
          const act = activities[i];
          if (act.existingId && oldIds.has(act.existingId)) {
            await supabase
              .from("path_activity")
              .update({
                activity_id: act.activityId,
                order_index: i,
              })
              .eq("id", act.existingId);
            keptIds.add(act.existingId);
          } else {
            await supabase.from("path_activity").insert({
              path_id: pathRecord.id,
              activity_id: act.activityId,
              order_index: i,
            });
          }
        }

        // Delete removed
        for (const oldId of oldIds) {
          if (!keptIds.has(oldId)) {
            await supabase.from("path_activity").delete().eq("id", oldId);
          }
        }
      } else {
        // CREATE
        const { data, error } = await supabase
          .from("path")
          .insert({ ...pathPayload, created_by: user.id })
          .select()
          .single();
        if (error) throw error;
        pathRecord = data;

        for (let i = 0; i < activities.length; i++) {
          const act = activities[i];
          await supabase.from("path_activity").insert({
            path_id: pathRecord.id,
            activity_id: act.activityId,
            order_index: i,
          });
        }
      }

      // Cascade publish to all activities if publishing
      if (publish) {
        await cascadePublish(pathRecord.id);
      }

      return pathRecord;
    },
    onSuccess: (_, { publish }) => {
      toast.success(publish ? "Parcours publié" : "Parcours sauvegardé");
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      queryClient.invalidateQueries({ queryKey: ["path-editor", pathId] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return {
    formData,
    setFormData,
    activities,
    setActivities,
    isLoading,
    isSaving: saveMutation.isPending,
    isPublished,
    save: (publish: boolean) => saveMutation.mutateAsync({ publish }),
  };
}
